import { colors } from '../constants';
import { formatStatusLabel, getStatusColor } from './statusUtils';
import { normalizeLifecycleStatus } from './queryListUtils';
import { LIFECYCLE_STATUS_STYLES, REGION_OPTIONS } from '../constants/assignedQueriesPage';

/**
 * Get lifecycle status metadata (label and color)
 * @param {string} statusLabel - Status label
 * @returns {Object|null} Status metadata or null
 */
const getLifecycleStatusMeta = (statusLabel) => {
  if (!statusLabel) return null;
  const key = normalizeLifecycleStatus(statusLabel);
  return LIFECYCLE_STATUS_STYLES[key] || null;
};

/**
 * Get display status for a query
 * @param {Object} query - Query object
 * @returns {Object|null} Status info with label and color
 */
export const getDisplayStatus = (query) => {
  if (!query) return null;

  const rawStatus = query.statusLabel || query.status;
  if (!rawStatus) return null;

  const lifecycleMeta = getLifecycleStatusMeta(rawStatus);
  if (lifecycleMeta) {
    return {
      label: lifecycleMeta.label,
      color: lifecycleMeta.color,
    };
  }

  const formattedLabel = formatStatusLabel(rawStatus, { uppercase: true });
  const color = getStatusColor(rawStatus) || colors.textSecondary;

  return {
    label: formattedLabel,
    color,
  };
};

/**
 * Format query type for display
 * @param {string} queryType - Query type value
 * @returns {string} Formatted query type
 */
export const formatQueryType = (queryType) => {
  if (!queryType) return 'N/A';
  return queryType
    .toString()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Get assignor information from query
 * @param {Object} query - Query object
 * @param {Object} viewer - Current user/viewer
 * @returns {Object} Assignor info with name, designation, isSelf
 */
export const getAssignorInfo = (query, viewer) => {
  if (!query) {
    return { name: '', designation: '', isSelf: false };
  }

  const assignor =
    query.assignor ||
    query.originalAssignee ||
    query.originalAssignor ||
    null;

  const viewerId = viewer?.id || viewer?._id || viewer?._Id;
  const assignorId =
    assignor?._id ||
    assignor?.id ||
    (typeof assignor === 'string' ? assignor : null);
  const isSelf =
    viewerId &&
    assignorId &&
    assignorId.toString().toLowerCase() === viewerId.toString().toLowerCase();

  return {
    name: assignor?.full_name ||
      assignor?.name ||
      assignor?.fullName ||
      assignor?.username ||
      '',
    designation: assignor?.designation || assignor?.role || '',
    isSelf,
  };
};

/**
 * Normalize ID value
 * @param {*} value - ID value (string, object with _id/id, etc.)
 * @returns {string|null} Normalized ID or null
 */
export const normalizeId = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  return value._id || value.id || null;
};

/**
 * Get visible region options based on user role
 * @param {string} currentUserRole - Current user role
 * @param {string} currentUserRegion - Current user region
 * @param {Array} regionOptions - All region options
 * @returns {Array} Filtered region options
 */
export const getVisibleRegionOptions = (currentUserRole, currentUserRegion, regionOptions = REGION_OPTIONS) => {
  const normalizedRole = (currentUserRole || '').toString().trim().toUpperCase();
  
  // CH (Chairperson) and MP (Member of Parliament) see all regions
  if (normalizedRole === 'CH' || normalizedRole === 'MP' || normalizedRole === 'CHAIRPERSON' || normalizedRole === 'CHAIRMAN') {
    return regionOptions;
  }
  
  // ED (Executive Director) and GM (General Manager) see only their region
  if (normalizedRole === 'ED' || normalizedRole === 'GM') {
    if (currentUserRegion) {
      const userRegionOption = regionOptions.find(
        (option) => option.value === currentUserRegion
      );
      return userRegionOption ? [userRegionOption] : regionOptions;
    }
    return regionOptions;
  }
  
  // Default: show all regions
  return regionOptions;
};

/**
 * Determine if Assigned By column should be shown
 * @param {Array} queries - Array of queries
 * @param {Object} currentUser - Current user object
 * @returns {boolean} True if column should be shown
 */
export const shouldShowAssignedByColumn = (queries, currentUser) => {
  const currentRole = (currentUser?.role || '').toString().trim().toLowerCase();
  if (currentRole === 'mp') {
    return false;
  }

  if (!Array.isArray(queries) || queries.length === 0) {
    return false;
  }
  return queries.some((query) => {
    const assignorInfo = getAssignorInfo(query, currentUser);
    return assignorInfo.name && !assignorInfo.isSelf;
  });
};
